import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Checkbox,
  FormControlLabel,
  Divider,
  Stack,
  Tooltip,
  Chip,
} from "@mui/material";
import CustomSearch from "../../Components/CustomSearch";
import CustomTab from "../../Components/CustomTab";
import PreviewComponent from "../mockTest/QuestionFormComponentMock/previewComponent";
import { FontFamily } from "../contentFeedback/Preview/getAlphabetForIndex";
import { Delete } from "@mui/icons-material";
import CustomIconButton from "../../Components/CustomIconButton";
import ClusterConfigDialog from "./ClusterConfigDialog";
import apiRequest from "../../request/apiRequest/apiRequest";
import { allApis } from "../../request/config";
import { fetchAllMastersLocal } from "../../store/slices/detailedMaster/detailedMasterSlice";
import SvgPreview from "../../Components/SvgPreview";
import { cross } from "../../assets/svgs";
import { subHeading } from "../../Components/CustomTexts";
import { displayRow, rowHorizontalEnd, rowSB } from "../../GlobalStyles";
import CustomContainedButton from "../../Components/CustomContainedButton";
import GlobalLoader from "../../Components/GlobalLoader";
import { showSnack } from "../../store/slices/constants";
import { useAppDispatch } from "../../hooks/storeHooks";
import useCopiedData from "../../store/slices/constants/staticContents/useCopiedData";

interface DistributeQuestionstoClustersProps {
  open: boolean;
  onClose: () => void;
  onPaste?: (response: any) => void;
  hierarchy?: any;
  setLoader: any;
}

const DistributeQuestionstoClusters: React.FC<
  DistributeQuestionstoClustersProps
> = ({ open, onClose, onPaste, hierarchy, setLoader }) => {
  const dispatch = useAppDispatch();
  const { copiedData, setCopiedData } = useCopiedData();
  const [clusters, setSubtopics] = useState<any[]>([]);
  const [questions, setQue] = useState<any[]>([]);
  useEffect(() => {
    try {
      const stored = localStorage.getItem("copied");
      if (stored) {
        const decoded = JSON.parse(decodeURIComponent(escape(atob(stored))));
        if (Array.isArray(decoded) && decoded.length > 0)
          setCopiedData(decoded);
      }
    } catch (err) {
      console.warn("Error reading copied data:", err);
    }
  }, []);
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [assigned, setAssigned] = useState<any>({});
  const [filteredSubtopics, setFilteredSubtopics] = useState(clusters || []);
  const [selectedTabId, setSelectedTabId] = useState<number>(0);
  const [clusterDialogOpen, setClusterDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    if (hierarchy && hierarchy.topic_id && open) {
      const FETCHSub = async () => {
        const payload: any = {
          level: "cluster",
          parent_id: hierarchy.subtopic_id,
        };

        const res: any = await fetchAllMastersLocal(payload);
        setSubtopics(res.clusters);
      };
      setAssigned({});
      FETCHSub();
    }
  }, [hierarchy, open]);
  useEffect(() => {
    const init: Record<string, string[]> = {};
    if (clusters.length && clusters.length > 0) {
      clusters.forEach((s: any) => (init[s.id] = []));

      setSelectedQuestions([]);
      setFilteredSubtopics(clusters || []);
      setSelectedTabId(0);
    } else {
    }
  }, [clusters, copiedData]);
  const handleChildDelete = (parentIdx: number, idToRemove: any) => {
    const updated = copiedData.map((item, idx) => {
      if (idx !== parentIdx) return item;

      // 🔹 If it's a parent-type item
      if (item.parent && Array.isArray(item.childs)) {
        return {
          ...item,
          childs: item.childs.filter((child: any) => child.id !== idToRemove),
        };
      }

      // 🔹 If it's a question-type item
      if (Array.isArray(item.questions)) {
        return {
          ...item,
          questions: item.questions.filter((q: any) => q.id !== idToRemove),
        };
      }

      return item;
    });

    setCopiedData(updated);

    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(updated))));
    localStorage.setItem("copied", encoded);
  };
  const groupedQuestions = useMemo(
    () =>
      (copiedData || []).map((c) => ({
        ...c,
        heading: `Source ${c.parent?.type??""} ${c.parent?.label??""}`,
        questions:
          (c.childs || []).map((q: any, idx: number) => ({
            ...q,
            displayIndex: idx + 1,
            _groupHeading: `${c.parent?.type??""} ${c.parent?.label??""}`,
          })) || [],
      })),
    [copiedData]
  );

  const allQuestions = useMemo(
    () =>
      groupedQuestions.flatMap((g) => g.questions.map((q: any) => ({ ...q }))),
    [groupedQuestions]
  );

  const assignToSubtopic = (cluster: any) => {
    setAssigned(cluster);
  };
  useEffect(() => {
    if (assigned && assigned.id) {
      const FETCHSub = async () => {
        const payload: any = {
          level: "question",
          parent_id: assigned.id,
        };

        const res: any = await fetchAllMastersLocal(payload);
        setQue(res.questions);
      };
      FETCHSub();
    }
  }, [assigned]);

  // 🔹 On Submit → Open Cluster Config Dialog
  const handleOpenClusterDialog = () => {
    // setClusterDialogOpen(true);
    // assigned
  };

  // 🔹 Final API Submit
  const handleClusterSubmit = async (form: any) => {
    setLoader(true);
    setIsSubmitting(true);
    try {
      const res: any = await apiRequest({
        api: allApis.move,
        payload: {
          level: "question",
          move_ids: copiedData[0].childs.map((s: any) => s.id) ?? [],
          parent_id: assigned.id,
        },
        method: "PUT",
      });
      if (res.error) {
        setLoader(false);
        dispatch(
          showSnack({
            open: true,
            severity: "error",
            message: res.msg,
          })
        );
      } else {
        if (onPaste) onPaste(res);
        onClose();
      }
    } catch (error) {
      console.error("Error submitting clusters:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 🔹 Final API Submit and Publish
  const handleClusterSubmitAndPublish = async (form: any) => {
    setLoader(true);
    setIsSubmitting(true);
    try {
      const res: any = await apiRequest({
        api: allApis.move,
        payload: {
          level: "question",
          move_ids: copiedData[0].childs.map((s: any) => s.id) ?? [],
          parent_id: assigned.id,
          is_publish: true,
        },
        method: "PUT",
      });
      if (res.error) {
        setLoader(false);
        dispatch(
          showSnack({
            open: true,
            severity: "error",
            message: res.msg,
          })
        );
      } else {
        if (onPaste) onPaste(res);
        onClose();
      }
    } catch (error) {
      console.error("Error submitting and publishing clusters:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const assignedContent = (
    <Box
      p="2px"
      sx={{
        height: "100%",
        boxSizing: "border-box",
        flexDirection: "column",
        display: "flex",
      }}
    >
      {groupedQuestions.map((group, gIndex) => {
        const groupAssigned = group.questions;
        if (!groupAssigned.length) return null;
        return (
          <Box
            key={`ag-${gIndex}`}
            sx={{ height: "100%", flexDirection: "column", display: "flex" }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              {group.heading}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Box
              sx={{ flex: 8, overflowY: "auto" }}
              className="scrollBluePrimarythin"
            >
              {" "}
              {groupAssigned.map((q: any) => {
                return (
                  <Paper
                    key={`assigned-${q.id}`}
                    elevation={0}
                    sx={{
                      p: 1.25,
                      mb: 1,
                      borderRadius: 1.5,
                      border: "1px solid #eee",
                      backgroundColor: "#fbfbfb",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Box
                        sx={{
                          width: 56,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography sx={{ fontWeight: 600 }}>
                          Q{q.displayIndex}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          component="div"
                          sx={{ fontSize: "15px", lineHeight: 1.4 }}
                        >
                          <PreviewComponent htmlContent={q.text} />
                        </Typography>
                        <Box
                          sx={{
                            mt: 0.75,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <CustomIconButton
                            content={<Delete />}
                            onClick={() => {}}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  const renderSubtopicCard = (s: any) => {
    return (
      <Paper
        key={s.id}
        elevation={0}
        sx={{
          border: "1px solid #e6e6e6",
          borderRadius: 2,
          p: 1.5,
          mb: 1.5,
          cursor: "pointer",
          backgroundColor: assigned?.id === s.id ? "#e6f7ff" : "#fff",
          transition: "background-color 150ms ease",
          "&:hover": {
            backgroundColor: assigned?.id === s.id ? "#dff4ff" : "#f9f9f9",
          },
        }}
        onClick={() => assignToSubtopic(s)}
      >
        {/* 🔹 Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography sx={{ fontWeight: 700, fontSize: "15px" }}>
            {s.name}
          </Typography>
          <Chip
            label={s.type ? s.type.toUpperCase() : "N/A"}
            size="small"
            color="primary"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              textTransform: "capitalize",
            }}
          />
        </Stack>

        {/* 🔹 Meta Details */}
        <Box sx={{ mt: 0.75, ml: 0.5 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Description: {s.description || "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Exposure Index: {s.exposure_index ?? "—"} | LOD Index:{" "}
            {s.lod_index ?? "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Min Time: {s.min_time ?? "—"} sec | Max Time: {s.max_time ?? "—"}{" "}
            sec
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Total Questions: {s.total_questions ?? 0}
          </Typography>
        </Box>
      </Paper>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullScreen    onContextMenu={(e) => {
          e.stopPropagation();
          e.preventDefault();}}>
        <DialogTitle
          sx={{ m: 0, p: "20px 15px 15px", ...rowSB }}
          id="customized-dialog-title"
        >
          {subHeading({
            text: "Move Questions to a cluster",
            size: "1rem",
            weight: "500",
          })}
          <CustomIconButton
            onClick={onClose}
            noHover={true}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
            content={<SvgPreview svg={cross} />}
          />
        </DialogTitle>

        <DialogContent dividers>
          {isSubmitting ? <GlobalLoader open={true} /> : ""}
          <Stack sx={{ height: "100%", ...displayRow, width: "100%" }}>
            <Box width="calc(33% - 5px)" height="100%" mr="10px">
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  boxShadow: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  boxSizing: "border-box",
                }}
              >
                {/* <CustomSearch
                  placeholder="Search question by text, ID or Q number"
                  copyData={allQuestions}
                  fieldName={["text","id","displayIndex"]}
                  filteredData={handleQuestionSearch}
                  onClear={() => setFilteredQuestions(allQuestions)}
                /> */}

                {assignedContent}
              </Paper>
            </Box>

            <Box width="calc(33% - 5px)" height="100%">
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  boxShadow: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  boxSizing: "border-box",
                }}
              >
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                  Clusters of {hierarchy ? hierarchy.topic ?? "" : ""}
                </Typography>

                <CustomSearch
                  copyData={clusters || []}
                  fieldName={[
                    "name",
                    "id",
                    "exposure_index",
                    "min_time",
                    "max_time",
                    "lod_index",
                  ]}
                  filteredData={(arr: any[]) => setFilteredSubtopics(arr)}
                  onClear={() => setFilteredSubtopics(clusters || [])}
                />

                <Box
                  className="scrollBluePrimarythin"
                  sx={{ mt: 1, flex: 8, overflowY: "auto" }}
                >
                  {(filteredSubtopics || []).length ? (
                    filteredSubtopics.map((s: any) => renderSubtopicCard(s))
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      No clusters found
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Box>
            <Box width="calc(34% - 5px)" height="100%">
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  boxShadow: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  boxSizing: "border-box",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    Destination Cluster’s Old Questions
                  </Typography>
                  <Chip
                    label={`${questions?.length || 0} Question${
                      questions?.length === 1 ? "" : "s"
                    }`}
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ fontSize: "0.75rem" }}
                  />
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Box
                  className="scrollBluePrimarythin"
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    pr: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  {questions && questions.length > 0 ? (
                    questions.map((q: any, index: number) => (
                      <Paper
                        key={q.id || index}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1.5,
                          backgroundColor: "#fafafa",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "#f5faff",
                            borderColor: "#90caf9",
                          },
                        }}
                      >
                        <Stack direction="row" spacing={1.5}>
                          <Box
                            sx={{
                              minWidth: 40,
                              maxHeight: "40px",
                              minHeight: "40px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "50%",
                              fontWeight: 600,
                              color: "#1976d2",
                              backgroundColor: "#e3f2fd",
                              fontSize: "0.85rem",
                            }}
                          >
                            Q{index + 1}
                          </Box>

                          <Box sx={{ flex: 1 }}>
                            <PreviewComponent htmlContent={q.text} />
                          </Box>
                        </Stack>
                      </Paper>
                    ))
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 2,
                        textAlign: "center",
                        fontStyle: "italic",
                      }}
                    >
                      No questions available for this cluster.
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ m: 0, p: "10px", ...rowHorizontalEnd }}>
          <CustomIconButton onClick={onClose} content="Cancel" />
          <CustomContainedButton
            content={isSubmitting ? "Submitting..." : "Proceed"}
            otherProps={{
              disabled: assigned && assigned.id ? false : isSubmitting || true,
              onClick: handleClusterSubmit,
            }}
          />
          <CustomContainedButton
            content={isSubmitting ? "Submitting..." : "Proceed And Publish"}
            otherProps={{
              disabled: assigned && assigned.id ? false : isSubmitting || true,
              onClick: handleClusterSubmitAndPublish,
            }}
          />
        </DialogActions>
      </Dialog>

      {/* 🔹 Cluster Config Modal */}
      {/* <ClusterConfigDialog
      
        open={clusterDialogOpen}
        onClose={() => setClusterDialogOpen(false)}
        onConfirm={handleClusterSubmit}
      /> */}
    </>
  );
};

export default DistributeQuestionstoClusters;
